import { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { ChatbotPath } from 'entities/ExportRouters'
import { IntegrationKey } from 'entities/Integrations'
import { type Conversation } from 'entities/Conversation'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { ChatbotApi } from 'entities/Chatbot'

export interface IConversationCalendlyAlertProps {
  conversation: Conversation
}

export const ConversationCalendlyAlert = observer(
  ({ conversation }: IConversationCalendlyAlertProps) => {
    const { recent_message } = conversation
    const { integrationsStore, eventLogStore, organizationStore } = useEntitiesContext()
    const calendlyIntegration = integrationsStore.getIntegration(IntegrationKey.calendly)
    const hubspotIntegration = integrationsStore.getIntegration(IntegrationKey.hubspot)
    const [hasActiveBookingAgent, setHasActiveBookingAgent] = useState(true)
    const [isBannerDismissed, setIsBannerDismissed] = useState(false)

    useEffect(() => {
      ChatbotApi.getBannerStatus()
        .then(({ data }) => {
          setIsBannerDismissed(data.is_dismissed)
          setHasActiveBookingAgent(data.has_booking_agent || data.has_unified_agent_with_booking)
        })
        .catch(() => {
          setIsBannerDismissed(false)
          setHasActiveBookingAgent(false)
        })
    }, [])

    const isCalendlyIntegrationConnected = calendlyIntegration && calendlyIntegration.isConnected
    const isHubspotIntegrationConnected = hubspotIntegration && hubspotIntegration.isConnected
    const isMessageIncludesLink =
      recent_message?.user_id &&
      (recent_message.body?.includes('calendly.com') ||
        recent_message.body?.includes('hubspot.com'))

    const isShouldShowAlert =
      isCalendlyIntegrationConnected || isHubspotIntegrationConnected || isMessageIncludesLink

    const shouldShowAlert = () =>
      !isShouldShowAlert || hasActiveBookingAgent || isBannerDismissed ? false : true

    const handleDismiss = () => {
      ChatbotApi.setDismiss()
      setIsBannerDismissed(true)
    }

    const handleSetUpAgentClick = () => {
      eventLogStore.logEvent(
        'Calendly Banner Used',
        {
          event_id: 'calendly_banner_used',
          action: 'Booking Agent Requested',
          agencyId: organizationStore.agency_id,
        },
        { groupId: organizationStore.id }
      )

      uiStore.changeRouteViaVue({
        path: `/${ChatbotPath.all}`,
      })
    }

    if (!shouldShowAlert()) return null

    return (
      <Alert
        item={{
          type: 'infoStars',
          title: 'Let AI Book More Meetings—Automatically',
          desc: 'Streamline your scheduling with AI Scheduler, your always-on Booking Agent. Just connect your calendar, and it will handle coordination, confirmations, reschedules, and follow-ups—so you can focus on closing, not chasing.',
        }}
        actions={[
          {
            type: 'primary',
            variant: 'button',
            text: 'Set up AI Scheduler',
            onClick: handleSetUpAgentClick,
          },
          {
            type: 'secondary',
            variant: 'button',
            text: 'Don’t show again',
            onClick: handleDismiss,
          },
        ]}
      />
    )
  }
)
